// Which provider a service opens on in the New Appointment dialog.
//
// The dialog picked the first provider in the list for every service, so a
// consultation opened on the nurse and a lab draw on the doctor — and the
// length followed that wrong pairing (the doctor's consultation is 90
// minutes; the service alone says 60). The practice's rule — lab work is
// Ellen's, everything else is Dr. Levitan's — is what the service-provider
// pairings in Admin say, so the dialog follows them: keep the selected
// provider when they are paired with the service, else the first provider
// paired with it, and null when nobody is (leave the choice alone).
export interface ServicePairing { serviceId: string; specialistId: string }

export function providerForService(
  serviceId: string,
  pairings: ServicePairing[],
  selectedSpecialistId: string,
): string | null {
  const paired = pairings.filter((p) => p.serviceId === serviceId);
  if (paired.length === 0) return null;
  if (selectedSpecialistId && paired.some((p) => p.specialistId === selectedSpecialistId)) return selectedSpecialistId;
  return paired[0].specialistId;
}

export interface NewAppointmentDefaults {
  services: Array<{ id: string; name: string }>;
  specialists: Array<{ id: string; name: string }>;
  pairings: ServicePairing[];
  current: { serviceId: string; specialistId: string; appointmentType: string };
}

/**
 * What a new appointment opens on when nothing has been chosen yet: the
 * first service, and the provider paired with it — not the first provider in
 * the list. Null when there is nothing to fill in: both already chosen, or
 * an imported row whose recorded type is not one of our services (the empty
 * Select explains itself in that case, and must stay empty).
 */
export function defaultNewAppointmentPairing(d: NewAppointmentDefaults): { serviceId: string; specialistId: string } | null {
  const { services, specialists, pairings, current } = d;
  if (services.length === 0 || specialists.length === 0) return null;
  if (current.serviceId && current.specialistId) return null;
  if (!current.serviceId && current.appointmentType) return null;
  const serviceId = current.serviceId || services[0].id;
  const paired = providerForService(serviceId, pairings, current.specialistId);
  const specialistId = (paired && specialists.some((s) => s.id === paired)) ? paired : (current.specialistId || specialists[0].id);
  if (serviceId === current.serviceId && specialistId === current.specialistId) return null;
  return { serviceId, specialistId };
}
